import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, User, ChevronDown, ShieldCheck } from 'lucide-react';
import TrripLogo from '../../assets/Trrip_logo/4triplogo_white.png';

const PartnerNavbar = ({ portalType = 'shop', subtitle = 'Management', userData = {}, walletBalance = 0, onMenuClick }) => {
  const navigate = useNavigate();

  const getPortalPath = (path) => {
    const pType = (portalType || 'shop').toLowerCase();
    return `/${pType}/${path}`;
  };

  const businessTitle = userData?.business_name || userData?.owner_name || `My ${portalType.charAt(0).toUpperCase() + portalType.slice(1)}`;

  const getDisplaySubtitle = () => {
    if (subtitle && subtitle.toLowerCase() !== 'management') {
      return subtitle.toUpperCase();
    }
    const typeMap = {
      restaurant: 'RESTAURANT MANAGEMENT',
      activity: 'ACTIVITY MANAGEMENT',
      shop: 'RETAIL SHOP MANAGEMENT',
    };
    return typeMap[(portalType || 'shop').toLowerCase()] || `${(portalType || 'partner').toUpperCase()} MANAGEMENT`;
  };

  return (
    <header className="bg-[#067978] shadow-xl border-b border-white/15 px-2 sm:px-6 lg:px-8 py-2 sm:py-3 flex-shrink-0 z-[100] relative overflow-hidden transition-all duration-300">
      
      {/* Background Subtle Gradient & Glow Orbs */}
      <div className="absolute top-0 right-1/4 w-64 h-64 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-10 w-48 h-48 bg-teal-300/10 rounded-full blur-2xl pointer-events-none"></div>

      <div className="max-w-[1920px] mx-auto flex items-center justify-between gap-1.5 sm:gap-4 relative z-10">
        
        {/* Left Section: Trrip Logo (Shows Logo only on < 1024px, adds Partner Portal + Subtitle on >= 1024px) */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <div className="flex items-center gap-2 lg:gap-4 bg-white/10 backdrop-blur-md px-2.5 py-1.5 lg:px-5 lg:py-2 rounded-xl lg:rounded-2xl border border-white/20 shadow-sm shrink-0">
            <img
              src={TrripLogo}
              alt="Trrip Logo"
              className="h-7 sm:h-9 lg:h-12 w-auto object-contain brightness-0 invert drop-shadow-sm"
            />
            <div className="hidden lg:block w-[1px] h-7 lg:h-9 bg-white/30 rounded-full"></div>
            <div className="hidden lg:flex flex-col justify-center pr-1 lg:pr-2">
              <h1 className="text-xs lg:text-lg font-medium text-white tracking-tight leading-tight">
                Partner Portal
              </h1>
              <p className="text-[8px] lg:text-[10px] text-emerald-100/80 font-extrabold uppercase tracking-widest mt-0.5 leading-none">
                {getDisplaySubtitle()}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Wallet & Profile Avatar */}
        <div className="flex items-center gap-1.5 sm:gap-4 shrink-0">
          
          {/* Wallet Badge Card */}
          <div
            onClick={() => navigate(getPortalPath('wallet'))}
            className="flex items-center gap-1 sm:gap-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md px-2 py-1 sm:px-4 sm:py-2 rounded-lg sm:rounded-2xl border border-white/20 cursor-pointer transition-all duration-300 group shadow-sm active:scale-95 shrink-0"
            title="Click to manage Wallet & Earnings"
          >
            <div className="p-1 sm:p-1.5 bg-emerald-400/20 rounded-md sm:rounded-xl group-hover:bg-emerald-400/30 group-hover:scale-110 transition-all duration-300 shrink-0">
              <Wallet className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-300" />
            </div>
            <div>
              <p className="text-[8px] sm:text-[9px] text-teal-100/70 font-black uppercase tracking-widest mb-0.5 hidden sm:block">
                Wallet
              </p>
              <p className="text-[11px] sm:text-sm font-black text-white group-hover:text-emerald-300 transition-colors leading-none">
                ₹{walletBalance.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-white/15 hidden sm:block"></div>

          {/* User Profile / Avatar Card */}
          <div
            onClick={() => navigate(getPortalPath('profile'))}
            className="flex items-center gap-1.5 sm:gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-md p-1 sm:pl-3 sm:pr-4 sm:py-1.5 rounded-lg sm:rounded-2xl border border-white/20 cursor-pointer transition-all duration-300 group shadow-sm active:scale-95 select-none shrink-0"
            title="Click to view Profile"
          >
            <div className="relative shrink-0">
              <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-md sm:rounded-xl bg-gradient-to-br from-white/20 to-white/10 border border-white/30 flex items-center justify-center overflow-hidden shadow-inner ring-2 ring-emerald-400/30 group-hover:ring-emerald-400/60 transition-all">
                {userData?.logo_url ? (
                  <img
                    src={userData.logo_url}
                    alt={businessTitle}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-white/90" />
                )}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full bg-emerald-400 border-2 border-[#067978] shadow-sm"></div>
            </div>

            <div className="text-right hidden sm:block">
              <p className="text-xs sm:text-sm font-normal text-white/90 group-hover:text-emerald-300 transition-colors tracking-tight truncate max-w-[120px] md:max-w-[160px]">
                {businessTitle}
              </p>
              <div className="flex items-center justify-end gap-1 text-[10px] text-teal-100/70 font-extrabold uppercase tracking-wider group-hover:text-white transition-colors">
                <span>View Profile</span>
                <ChevronDown className="w-3 h-3 group-hover:translate-y-0.5 transition-transform" />
              </div>
            </div>
          </div>

        </div>

      </div>
    </header>
  );
};

export default PartnerNavbar;
